import { type ComponentProps } from 'react';

import {
	Checkbox as ArkCheckbox,
	type CheckboxGroupProps as ArkCheckboxGroupProps
} from '@ark-ui/react';
import { Check } from '@phosphor-icons/react/dist/csr/Check';

import type { ActionColor, Scale3 } from '../../lib/types';
import { cn } from '../../lib/utils';

type CheckboxGroupProps = Omit<ArkCheckboxGroupProps, 'color'> & {
	size?: Scale3;
	variant?: 'classic' | 'surface' | 'soft';
	color?: ActionColor;
	highContrast?: boolean;
};

function CheckboxGroupRoot({
	className,
	size = '2',
	variant = 'surface',
	color = 'accent',
	highContrast,
	...props
}: CheckboxGroupProps) {
	return (
		<ArkCheckbox.Group
			className={cn('pt-checkbox-group', className)}
			data-size={size}
			data-variant={variant}
			data-accent-color={color}
			data-high-contrast={highContrast ? '' : undefined}
			{...props}
		/>
	);
}

function CheckboxGroupItem({ className, ...props }: ComponentProps<typeof ArkCheckbox.Root>) {
	return <ArkCheckbox.Root className={cn('pt-checkbox-group-item', className)} {...props} />;
}

function CheckboxGroupItemControl({
	className,
	...props
}: ComponentProps<typeof ArkCheckbox.Control>) {
	return (
		<ArkCheckbox.Control className={cn('pt-checkbox-group-item-control', className)} {...props}>
			<ArkCheckbox.Indicator>
				<Check size={12} weight="bold" />
			</ArkCheckbox.Indicator>
		</ArkCheckbox.Control>
	);
}

function CheckboxGroupItemLabel({ className, ...props }: ComponentProps<typeof ArkCheckbox.Label>) {
	return <ArkCheckbox.Label className={cn('pt-checkbox-group-item-label', className)} {...props} />;
}

const CheckboxGroup = Object.assign(CheckboxGroupRoot, {
	Root: CheckboxGroupRoot,
	Item: CheckboxGroupItem,
	ItemControl: CheckboxGroupItemControl,
	ItemLabel: CheckboxGroupItemLabel,
	ItemHiddenInput: ArkCheckbox.HiddenInput
});

export { CheckboxGroup, type CheckboxGroupProps };
